import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { QrCode, Calendar, MapPin, ChevronRight, ShieldAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamCheckIn() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/team/me/events?feature=check_in`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEvents(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="team-checkin-page">
      <div className="max-w-xl mx-auto">
        <div className="mb-6">
          <Badge variant="outline" className="mb-2">Check-In Mode</Badge>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <QrCode className="w-7 h-7 text-indigo-600" /> Scan Tickets
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Pick the event you're working tonight.</p>
        </div>

        {loading && <div className="text-center text-muted-foreground py-10">Loading events…</div>}
        {error && (
          <Card><CardContent className="py-10 text-center">
            <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </CardContent></Card>
        )}
        {!loading && !error && events.length === 0 && (
          <Card><CardContent className="py-10 text-center text-muted-foreground">
            <QrCode className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No events available for check-in right now.</p>
            <p className="text-xs mt-2">Check-in access auto-expires 1 hour after an event ends.</p>
          </CardContent></Card>
        )}

        <div className="grid gap-3">
          {events.map(e => (
            <Link
              key={e.event_id}
              to={`/admin/checkin/${e.event_id}`}
              className="block"
              data-testid={`checkin-event-${e.event_id}`}
            >
              <Card className="hover:border-indigo-500 transition active:scale-[0.99]">
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-center gap-4">
                    {e.hero_image && (
                      <img src={e.hero_image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{e.title}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1 flex-wrap">
                        {e.event_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{e.event_date.slice(0, 10)}</span>}
                        {(e.city || e.venue) && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.venue || e.city}</span>}
                      </div>
                      {(e.tags?.length > 0 || e.category) && (
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {e.category && <Badge variant="secondary" className="text-xs">{e.category}</Badge>}
                          {e.tags?.slice(0, 3).map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
